import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MobileMenu } from '@/components/ui/mobile-menu';
import { storeAPI, serviceAPI, staffAPI, reviewAPI, waitlistAPI } from '../lib/api';
import { useStore } from '../context/StoreContext';
import { useBrand } from '../context/BrandContext';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import {
  Scissors,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  ShoppingBag,
  ArrowLeft,
  Star,
  Users,
  ChevronRight,
  Instagram,
  Facebook,
  Bell,
  Loader2
} from 'lucide-react';

export default function StorePage() {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const { selectStore, store, modules, branding, hasShop } = useStore();
  const { isSingleStore, storeSlug: configSlug, brand } = useBrand();
  const { user, isAuthenticated } = useAuth();
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Waitlist state
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    service_id: '',
    preferred_staff_id: '',
    preferred_date: '',
    preferred_times: [],
    notes: '',
    guest_name: '',
    guest_phone: '',
    guest_email: ''
  });

  // Use URL slug or config slug for single-store mode
  const slug = urlSlug || configSlug;

  // Generate links based on mode
  const getLink = (path) => {
    if (isSingleStore) {
      return path; // Direct path like /prenota
    }
    return `/s/${slug}${path}`; // Full path like /s/demo/prenota
  };

  useEffect(() => {
    if (slug) {
      loadStoreData();
    }
  }, [slug]);

  const loadStoreData = async () => {
    try {
      setLoading(true);
      await selectStore(slug);
      
      // Load services, staff and reviews
      const [servicesRes, staffRes, reviewsRes, statsRes] = await Promise.all([
        serviceAPI.list(),
        staffAPI.list(),
        reviewAPI.list().catch(() => ({ data: [] })),
        reviewAPI.getStats().catch(() => ({ data: null }))
      ]);
      
      setServices(servicesRes.data);
      setStaff(staffRes.data);
      setReviews(reviewsRes.data);
      setReviewStats(statsRes.data);
    } catch (error) {
      console.error('Errore caricamento negozio:', error);
      toast.error('Negozio non trovato');
      if (!isSingleStore) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    
    if (!waitlistForm.service_id) {
      toast.error('Seleziona un servizio');
      return;
    }
    
    // Guest validation
    if (!isAuthenticated) {
      if (!waitlistForm.guest_name || !waitlistForm.guest_phone) {
        toast.error('Inserisci nome e telefono');
        return;
      }
    }
    
    setWaitlistLoading(true);
    try {
      const payload = {
        service_id: waitlistForm.service_id,
        preferred_staff_id: waitlistForm.preferred_staff_id || null,
        preferred_date: waitlistForm.preferred_date || null,
        preferred_times: waitlistForm.preferred_times.length > 0 ? waitlistForm.preferred_times : null,
        notes: waitlistForm.notes || null
      };
      
      // Add guest info if not authenticated
      if (!isAuthenticated) {
        payload.guest_name = waitlistForm.guest_name;
        payload.guest_phone = waitlistForm.guest_phone;
        payload.guest_email = waitlistForm.guest_email || null;
      }
      
      await waitlistAPI.add(payload);
      toast.success('Richiesta aggiunta alla lista d\'attesa! Ti contatteremo presto.');
      setWaitlistOpen(false);
      setWaitlistForm({
        service_id: '',
        preferred_staff_id: '',
        preferred_date: '',
        preferred_times: [],
        notes: '',
        guest_name: '',
        guest_phone: '',
        guest_email: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'invio');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const togglePreferredTime = (time) => {
    setWaitlistForm(prev => ({
      ...prev,
      preferred_times: prev.preferred_times.includes(time)
        ? prev.preferred_times.filter(t => t !== time)
        : [...prev.preferred_times, time]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Negozio non trovato</p>
        {!isSingleStore && (
          <Button asChild>
            <Link to="/">Torna alla Home</Link>
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            {!isSingleStore && (
              <Link to="/" className="text-muted-foreground hover:text-foreground hidden md:block">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            )}
            <div className="flex items-center gap-2">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={store.name} className="w-8 h-8 rounded object-cover" />
              ) : (
                <Scissors className="w-6 h-6 text-primary" />
              )}
              <span className="font-semibold">{brand?.appName || store.name}</span>
            </div>
          </div>
          
          {/* Desktop buttons */}
          <div className="hidden md:flex items-center gap-2">
            {hasShop && (
              <Button variant="outline" size="sm" asChild data-testid="shop-btn">
                <Link to={getLink('/shop')}>
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Shop
                </Link>
              </Button>
            )}
            <Button size="sm" asChild data-testid="book-btn">
              <Link to={getLink('/prenota')}>
                <Calendar className="w-4 h-4 mr-2" />
                Prenota
              </Link>
            </Button>
            {isAuthenticated ? (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/dashboard">Account</Link>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/login">Accedi</Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu */}
          <MobileMenu />
        </div>
      </header>

      {/* Hero */}
      <section 
        className="pt-24 pb-12 section-padding"
        style={{
          background: `linear-gradient(135deg, ${branding?.primary_color || '#047857'}15 0%, transparent 100%)`
        }}
      >
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {!isSingleStore && (
                <Badge className="mb-4" variant={store.status === 'ACTIVE' ? 'default' : 'secondary'}>
                  {store.status === 'DEMO' ? 'Demo' : 'Attivo'}
                </Badge>
              )}
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">{store.name}</h1>
              {(branding?.tagline || store.description) && (
                <p className="text-lg text-muted-foreground mb-6">{branding?.tagline || store.description}</p>
              )}
              
              <div className="space-y-3 mb-8">
                {store.address && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-5 h-5" />
                    <span>{store.address}</span>
                  </div>
                )}
                {store.phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-5 h-5" />
                    <a href={`tel:${store.phone}`} className="hover:text-primary">{store.phone}</a>
                  </div>
                )}
                {store.email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-5 h-5" />
                    <a href={`mailto:${store.email}`} className="hover:text-primary">{store.email}</a>
                  </div>
                )}
              </div>
              
              {/* Social Links */}
              {branding?.social_links && (
                <div className="flex gap-3 mb-6">
                  {branding.social_links.instagram && (
                    <a href={branding.social_links.instagram} target="_blank" rel="noopener noreferrer" 
                       className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                      <Instagram className="w-5 h-5 text-primary" />
                    </a>
                  )}
                  {branding.social_links.facebook && (
                    <a href={branding.social_links.facebook} target="_blank" rel="noopener noreferrer"
                       className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                      <Facebook className="w-5 h-5 text-primary" />
                    </a>
                  )}
                </div>
              )}
              
              <div className="flex gap-4">
                <Button size="lg" asChild data-testid="cta-book-btn">
                  <Link to={getLink('/prenota')}>
                    <Calendar className="w-5 h-5 mr-2" />
                    Prenota Ora
                  </Link>
                </Button>
                {hasShop && (
                  <Button size="lg" variant="outline" asChild data-testid="cta-shop-btn">
                    <Link to={getLink('/shop')}>
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Vai allo Shop
                    </Link>
                  </Button>
                )}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1593702233354-259d1f794ed1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxiYXJiZXIlMjBjdXR0aW5nJTIwaGFpciUyMGNsb3NlJTIwdXAlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY4MjYwMTQwfDA&ixlib=rb-4.1.0&q=85"
                alt={store.name}
                className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-4 -right-4 glass-card p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Star className="w-5 h-5 text-primary fill-primary" />
                </div>
                <div>
                  <p className="font-bold">{reviewStats?.avg_rating || '5.0'}</p>
                  <p className="text-xs text-muted-foreground">{reviewStats?.total_reviews || 0} recensioni</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="container-main">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">I Nostri Servizi</h2>
              <p className="text-muted-foreground">Scegli il servizio che fa per te</p>
            </div>
            <Button variant="outline" asChild data-testid="view-all-services">
              <Link to={getLink('/prenota')}>
                Vedi tutti
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
          
          {services.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">Nessun servizio disponibile al momento.</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.slice(0, 6).map((service) => (
                <Card 
                  key={service.id} 
                  className="service-card overflow-hidden"
                  data-testid={`service-${service.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{service.name}</h3>
                        {service.description && (
                          <p className="text-sm text-muted-foreground mt-1">{service.description}</p>
                        )}
                      </div>
                      <span className="text-lg font-bold text-primary">
                        {formatPrice(service.price)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{service.duration_minutes} min</span>
                      </div>
                      <Button size="sm" asChild data-testid={`book-service-${service.id}`}>
                        <Link to={`${getLink('/prenota')}?service=${service.id}`}>
                          Prenota
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Staff */}
      <section className="section-padding bg-secondary/30">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">Il Nostro Team</h2>
            <p className="text-muted-foreground">Professionisti esperti al tuo servizio</p>
          </div>
          
          {staff.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">Team in arrivo...</p>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {staff.map((member) => (
                <Card key={member.id} className="card-hover text-center p-6" data-testid={`staff-${member.id}`}>
                  <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-4 flex items-center justify-center">
                    <Users className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="font-semibold">{member.user?.name || 'Operatore'}</h3>
                  {member.specializations?.length > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {member.specializations.join(', ')}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="section-padding">
          <div className="container-main">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">Recensioni Clienti</h2>
                <p className="text-muted-foreground">
                  {reviewStats?.avg_rating || 5} stelle su {reviewStats?.total_reviews || 0} recensioni
                </p>
              </div>
              <Button variant="outline" asChild data-testid="view-all-reviews">
                <Link to={getLink('/recensioni')}>
                  Vedi tutte
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((review) => (
                <Card key={review.id} className="card-hover">
                  <CardContent className="p-6">
                    <div className="flex gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                    {review.comment && (
                      <p className="text-muted-foreground mb-3 line-clamp-3">"{review.comment}"</p>
                    )}
                    <p className="text-sm font-medium">
                      {review.guest_name || 'Cliente verificato'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {review.service_name}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Waitlist Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <Card className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bell className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Non trovi disponibilità?</h3>
                  <p className="text-muted-foreground">
                    Iscriviti alla lista d'attesa e ti contatteremo quando si libera un posto
                  </p>
                </div>
              </div>
              
              <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" data-testid="waitlist-btn">
                    <Bell className="w-4 h-4 mr-2" />
                    Lista d'Attesa
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Iscriviti alla Lista d'Attesa</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                    {/* Guest info if not logged in */}
                    {!isAuthenticated && (
                      <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">I tuoi dati per essere contattato</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Nome *</Label>
                            <Input 
                              value={waitlistForm.guest_name}
                              onChange={(e) => setWaitlistForm({...waitlistForm, guest_name: e.target.value})}
                              placeholder="Il tuo nome"
                              data-testid="waitlist-guest-name"
                            />
                          </div>
                          <div>
                            <Label>Telefono *</Label>
                            <Input 
                              value={waitlistForm.guest_phone}
                              onChange={(e) => setWaitlistForm({...waitlistForm, guest_phone: e.target.value})}
                              placeholder="+39..."
                              data-testid="waitlist-guest-phone"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Email (opzionale)</Label>
                          <Input 
                            type="email"
                            value={waitlistForm.guest_email}
                            onChange={(e) => setWaitlistForm({...waitlistForm, guest_email: e.target.value})}
                            placeholder="tuaemail@esempio.com"
                          />
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <Label>Servizio *</Label>
                      <Select 
                        value={waitlistForm.service_id} 
                        onValueChange={(v) => setWaitlistForm({...waitlistForm, service_id: v})}
                      >
                        <SelectTrigger data-testid="waitlist-service-select">
                          <SelectValue placeholder="Seleziona servizio" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name} - {formatPrice(s.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Operatore preferito (opzionale)</Label>
                      <Select 
                        value={waitlistForm.preferred_staff_id || "any"} 
                        onValueChange={(v) => setWaitlistForm({...waitlistForm, preferred_staff_id: v === "any" ? "" : v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Qualsiasi operatore" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Qualsiasi</SelectItem>
                          {staff.map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.user?.name || 'Operatore'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Data preferita (opzionale)</Label>
                      <Input 
                        type="date"
                        value={waitlistForm.preferred_date}
                        onChange={(e) => setWaitlistForm({...waitlistForm, preferred_date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div>
                      <Label>Fascia oraria preferita</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {[
                          { id: 'morning', label: 'Mattina' },
                          { id: 'afternoon', label: 'Pomeriggio' },
                          { id: 'evening', label: 'Sera' }
                        ].map(time => (
                          <Button
                            key={time.id}
                            type="button"
                            variant={waitlistForm.preferred_times.includes(time.id) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => togglePreferredTime(time.id)}
                          >
                            {time.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label>Note aggiuntive (opzionale)</Label>
                      <Textarea 
                        value={waitlistForm.notes}
                        onChange={(e) => setWaitlistForm({...waitlistForm, notes: e.target.value})}
                        placeholder="Indicaci qualsiasi preferenza o richiesta particolare..."
                        rows={2}
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={waitlistLoading} data-testid="waitlist-submit-btn">
                      {waitlistLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Invio in corso...
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4 mr-2" />
                          Iscriviti alla Lista
                        </>
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-main">
          <Card 
            className="p-8 sm:p-12 text-center"
            style={{
              background: `linear-gradient(135deg, ${branding?.primary_color || '#047857'}10 0%, ${branding?.primary_color || '#047857'}05 100%)`
            }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Pronto a prenotare?</h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Scegli il servizio, l'operatore e l'orario che preferisci. La prenotazione richiede solo pochi secondi!
            </p>
            <Button size="lg" asChild data-testid="final-cta-book">
              <Link to={getLink('/prenota')}>
                <Calendar className="w-5 h-5 mr-2" />
                Prenota il tuo Appuntamento
              </Link>
            </Button>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="container-main flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt={store.name} className="w-5 h-5 rounded object-cover" />
            ) : (
              <Scissors className="w-5 h-5 text-primary" />
            )}
            <span className="text-sm">{branding?.footer_text || `© ${new Date().getFullYear()} ${store.name}`}</span>
          </div>
          {!isSingleStore && (
            <p className="text-sm text-muted-foreground">
              Powered by <Link to="/" className="text-primary hover:underline">AppLook</Link>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
